<template>
  <div
    v-if="show"
    ref="modalRef"
    class="badiu-tiptap-modal"
    tabindex="-1"
    @mousedown.self="onCancel"
    @keydown="onDialogKeydown"
  >
    <div class="badiu-tiptap-modal-dialog">
      <div
        class="badiu-tiptap-modal-content"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="dialogTitleId"
      >

        <div class="badiu-tiptap-modal-header">
          <h6 :id="dialogTitleId" class="badiu-tiptap-modal-title">
            <i class="fas fa-link" aria-hidden="true"></i>Insert / edit link
          </h6>
          <button
            type="button"
            class="badiu-tiptap-modal-close"
            aria-label="Close"
            @click="onCancel"
          >&times;</button>
        </div>

        <div class="badiu-tiptap-modal-body">

          <div class="badiu-tiptap-form-group">
            <label :for="hrefInputId" class="badiu-tiptap-form-label">URL</label>
            <div class="badiu-tiptap-input-with-action">
              <input
                :id="hrefInputId"
                ref="firstInputRef"
                v-model="form.href"
                type="text"
                class="badiu-tiptap-input"
                placeholder="https://..."
              />
              <badiu-tiptap-file-upload
                accept="*/*"
                filetype="file"
                title="Upload file and use URL"
                icon="fa-paperclip"
                :config="config"
                @uploaded="onUploaded"
                @error="onUploadError"
              />
            </div>
          </div>

          <div class="badiu-tiptap-form-group">
            <label :for="textInputId" class="badiu-tiptap-form-label">Display text</label>
            <input
              :id="textInputId"
              v-model="form.text"
              type="text"
              class="badiu-tiptap-input"
              placeholder="Link text"
            />
          </div>

          <div class="badiu-tiptap-form-group">
            <label :for="titleInputId" class="badiu-tiptap-form-label">Title (tooltip)</label>
            <input
              :id="titleInputId"
              v-model="form.title"
              type="text"
              class="badiu-tiptap-input"
              placeholder="Shown on hover"
            />
          </div>

          <div class="badiu-tiptap-form-group">
            <label :for="targetInputId" class="badiu-tiptap-form-label">Open in</label>
            <select :id="targetInputId" v-model="form.target" class="badiu-tiptap-select">
              <option value="">Same window (default)</option>
              <option value="_blank">New window (_blank)</option>
            </select>
          </div>

        </div>

        <div class="badiu-tiptap-modal-footer">
          <button
            v-if="hasExistingLink"
            type="button"
            class="badiu-tiptap-btn badiu-tiptap-btn-outline-danger"
            @click="onRemove"
          >
            <i class="fas fa-unlink" aria-hidden="true"></i> Remove link
          </button>

          <button
            type="button"
            class="badiu-tiptap-btn badiu-tiptap-btn-secondary"
            @click="onCancel"
          >
            Cancel
          </button>

          <button
            type="button"
            class="badiu-tiptap-btn badiu-tiptap-btn-primary"
            :disabled="!form.href"
            @click="onSave"
          >
            Save
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
/**
 * BadiuTiptapLink
 *
 * Isolated modal component for inserting and editing links — TinyMCE-style:
 *  - URL (with optional file upload)
 *  - Display text
 *  - Title (tooltip)
 *  - Target (same window / _blank)
 *  - Remove existing link
 */
import { computed, nextTick, reactive, ref, watch } from 'vue'
import BadiuTiptapFileUpload from './BadiuTiptapFileUpload.vue'

let linkDialogSequence = 0;

export default {
  name: "BadiuTiptapLink",

  components: {
    BadiuTiptapFileUpload
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },

    initial: {
      type: Object,
      default: () => ({})
    },

    /** Forwarded to BadiuTiptapFileUpload (ver BadiuTiptapEditor para doc das chaves). */
    config: {
      type: Object,
      default: () => ({})
    }
  },

  emits: ["save", "remove", "cancel", "uploaded", "error"],

  setup(props, { emit }) {
    const instanceId    = `badiu-tiptap-link-${++linkDialogSequence}`;
    const dialogTitleId = `${instanceId}-title`;
    const hrefInputId   = `${instanceId}-href`;
    const textInputId   = `${instanceId}-text`;
    const titleInputId  = `${instanceId}-link-title`;
    const targetInputId = `${instanceId}-target`;

    const modalRef      = ref(null);
    const firstInputRef = ref(null);
    let returnFocusElement = null;

    const form = reactive({
      href:   "",
      text:   "",
      title:  "",
      target: ""
    });

    const hasExistingLink = computed(() => !!props.initial.href);

    watch(
      () => props.show,
      (visible, wasVisible) => {
        if (visible) {
          if (typeof document !== "undefined") {
            returnFocusElement = document.activeElement;
          }

          form.href   = props.initial.href   || "";
          form.text   = props.initial.text   || "";
          form.title  = props.initial.title  || "";
          form.target = props.initial.target || "";

          nextTick(() => firstInputRef.value?.focus());
        } else if (wasVisible) {
          const focusTarget = returnFocusElement;
          returnFocusElement = null;

          nextTick(() => {
            if (focusTarget?.isConnected && typeof focusTarget.focus === "function") {
              focusTarget.focus();
            }
          });
        }
      },
      { immediate: true }
    );

    function onSave() {
      if (!form.href) return;

      emit("save", {
        href:   form.href,
        text:   form.text || form.href,
        title:  form.title  || null,
        target: form.target || null
      });
    }

    function onRemove() {
      emit("remove");
    }

    function onCancel() {
      emit("cancel");
    }

    function onDialogKeydown(event) {
      if (event.isComposing) return;

      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onCancel();
        return;
      }

      if (event.key !== "Tab" || typeof document === "undefined") return;

      const focusableElements = Array.from(
        modalRef.value?.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      ).filter((element) => element.offsetParent !== null && element.getAttribute("aria-hidden") !== "true");

      if (!focusableElements.length) {
        event.preventDefault();
        modalRef.value?.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement  = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (!focusableElements.includes(activeElement)) {
        event.preventDefault();
        (event.shiftKey ? lastElement : firstElement).focus();
      } else if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    function onUploaded(payload) {
      form.href = payload.url;

      if (!form.text && payload.name) {
        form.text = payload.name;
      }

      emit("uploaded", payload);
    }

    function onUploadError(payload) {
      emit("error", payload);
    }

    return {
      form,
      hasExistingLink,
      modalRef,
      firstInputRef,
      dialogTitleId,
      hrefInputId,
      textInputId,
      titleInputId,
      targetInputId,
      onSave,
      onRemove,
      onCancel,
      onDialogKeydown,
      onUploaded,
      onUploadError
    };
  }
};
</script>
